import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hasPermission, RequestCarrierState } from '@magimp/shared';
import { Button, Icon, Select, useNarrow } from '../../../../ui/index.js';
import { useAuthStore } from '../../../../stores/auth.js';
import {
  useAddCarrier,
  useCarriersCatalog,
  useDeleteCarrier,
  useDeleteCarrierBid,
  usePatchCarrier,
  usePickWinner,
} from '../../queries.js';
import {
  canAddFirstCarrier,
  canManageRequestCarriers,
  canPickWinner,
  canReviveCarriersOnCannotTransport,
} from '../../role.js';
import { errorMessage } from '../../../../api/errors.js';
import type {
  CarrierBid,
  CarrierBidQuote,
  RequestDestinationView,
  RequestView,
} from '../../types.js';
import { CarrierBidCard } from './CarrierBidCard.js';

export interface CarriersDestinationSectionProps {
  request: RequestView;
  destination: RequestDestinationView;
  cheapestBidId: string | null;
  fxRates: Record<string, number>;
  onAddBid: (bid: CarrierBid) => void;
  onEditBid: (bid: CarrierBid, quote: CarrierBidQuote) => void;
  onReject: (bid: CarrierBid) => void;
  onError: (message: string) => void;
}

/**
 * @aiPurpose render one destination's carrier list with its own picker.
 *   Per-destination scope (#702): the same carrier may appear in
 *   multiple destinations of one request, and each section's picker
 *   filters out only the carriers already attached to *this* city.
 */
export function CarriersDestinationSection({
  request: r,
  destination,
  cheapestBidId,
  fxRates,
  onAddBid,
  onEditBid,
  onReject,
  onError,
}: CarriersDestinationSectionProps) {
  const { t } = useTranslation(['requests', 'common']);
  const user = useAuthStore((s) => s.user);
  const permissions = useAuthStore((s) => s.permissions);
  // WHY (#702): mobile cards use the ≤3-char shortName to keep the
  //   section header readable; desktop keeps the full city name.
  const narrow = useNarrow();
  const addCarrierMutation = useAddCarrier(r.id);
  const patchCarrierMutation = usePatchCarrier(r.id);
  const deleteCarrierMutation = useDeleteCarrier(r.id);
  const deleteBidMutation = useDeleteCarrierBid(r.id);
  const pickWinnerMutation = usePickWinner(r.id);
  const [selectedCarrierId, setSelectedCarrierId] = useState('');

  const party = {
    actorUserId: user?.id ?? null,
    managerId: r.managerId,
    logistId: r.logistId,
  };
  const canManage = canManageRequestCarriers(permissions, r.status, party);
  // INVARIANT (#962): READY_FOR_WORK opens a single-action window where
  //   the logist-side party can add the FIRST carrier. The picker is
  //   the only carrier-affordance in this state — per-row manage
  //   actions (reject/delete/bid edit) require IN_WORK, gated on
  //   `canManage`. Counting against `r.carriers` (not the
  //   per-destination slice) mirrors the backend's auto-START trigger,
  //   which fires on the request's FIRST carrier across all
  //   destinations.
  const canAddFirst = canAddFirstCarrier(permissions, r.status, r.carriers.length, party);
  const canRevive = canReviveCarriersOnCannotTransport(permissions, r.status, party);
  const showPicker = canManage || canAddFirst || canRevive;
  const canReadCarrierCatalog = hasPermission(permissions, 'carriers.read');
  const catalogQuery = useCarriersCatalog({ enabled: showPicker && canReadCarrierCatalog });
  const showCTA = canPickWinner(permissions, r.status, r.winnerBidId != null, party);

  const carriersForCity = useMemo(
    () => r.carriers.filter((c) => c.arrivalCityId === destination.arrivalCityId),
    [r.carriers, destination.arrivalCityId],
  );
  const existingIds = useMemo(
    () => new Set(carriersForCity.map((c) => c.carrier.id)),
    [carriersForCity],
  );
  const addOptions = (catalogQuery.data?.items ?? [])
    .filter((c) => c.active && !existingIds.has(c.id))
    .map((c) => ({ value: c.id, label: c.name }));

  async function onAddCarrier(): Promise<void> {
    if (!selectedCarrierId) return;
    try {
      await addCarrierMutation.mutateAsync({
        carrierId: selectedCarrierId,
        arrivalCityId: destination.arrivalCityId,
      });
      setSelectedCarrierId('');
    } catch (err) {
      onError(errorMessage(err));
    }
  }

  async function onReset(bid: CarrierBid): Promise<void> {
    try {
      await patchCarrierMutation.mutateAsync({
        requestCarrierId: bid.id,
        body: { state: RequestCarrierState.PENDING },
      });
    } catch (err) {
      onError(errorMessage(err));
    }
  }

  async function onDelete(bid: CarrierBid): Promise<void> {
    try {
      await deleteCarrierMutation.mutateAsync(bid.id);
    } catch (err) {
      onError(errorMessage(err));
    }
  }

  async function onDeleteBid(bid: CarrierBid, quote: CarrierBidQuote): Promise<void> {
    try {
      await deleteBidMutation.mutateAsync({ requestCarrierId: bid.id, bidId: quote.id });
    } catch (err) {
      onError(errorMessage(err));
    }
  }

  return (
    <section
      aria-label={destination.arrivalCity.name}
      data-arrival-city-id={destination.arrivalCity.id}
      style={{ display: 'grid', gap: 10 }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
        }}
      >
        {/* WHY (#702): full city name on desktop, ≤3-char shortName on
            narrow viewports. Switching at the React level (via
            useNarrow) keeps the DOM minimal and avoids ssr-fragile
            CSS-only display swaps. */}
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--fg-1)' }}>
          {narrow
            ? t('requests:carriers.destinationSection.titleShort', {
                short: destination.arrivalCity.shortName,
              })
            : t('requests:carriers.destinationSection.titleFull', {
                name: destination.arrivalCity.name,
              })}
        </h3>
        <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>
          {/* WHY (#1058): completed = ANSWERED + REJECTED per destination. */}
          {t('carriers.completedCount', {
            completed: carriersForCity.filter(
              (c) => c.state === 'answered' || c.state === 'rejected',
            ).length,
            total: carriersForCity.length,
          })}
        </span>
      </header>

      {showPicker && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            alignItems: 'center',
          }}
        >
          <div style={{ flex: '1 1 220px', minWidth: 0 }}>
            <Select
              value={selectedCarrierId}
              placeholder={t('requests:carriers.addPlaceholder')}
              options={addOptions}
              onChange={setSelectedCarrierId}
            />
          </div>
          <Button
            kind="primary"
            icon="plus"
            disabled={!selectedCarrierId || addCarrierMutation.isPending}
            onClick={() => {
              void onAddCarrier();
            }}
          >
            {t('requests:carriers.add')}
          </Button>
          {catalogQuery.isError && (
            <span
              style={{
                fontSize: 12,
                color: 'var(--status-error-fg)',
                flexBasis: '100%',
                overflowWrap: 'anywhere',
              }}
            >
              {errorMessage(catalogQuery.error)}
            </span>
          )}
        </div>
      )}

      {carriersForCity.length === 0 ? (
        <div
          style={{
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8,
            color: 'var(--fg-3)',
            border: '1px dashed var(--border-2)',
            borderRadius: 6,
          }}
        >
          <Icon name="ship" size={20} style={{ color: 'var(--fg-4)' }} />
          <div style={{ fontSize: 12 }}>{t('carriers.empty')}</div>
        </div>
      ) : (
        <ul
          aria-label={t('requests:carriers.columns.carrier')}
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'grid',
            gap: 10,
          }}
        >
          {carriersForCity.map((c) => (
            <li key={c.id}>
              <CarrierBidCard
                bid={c}
                requestId={r.id}
                request={r}
                winnerBidId={r.winnerBidId}
                cheapestBidId={cheapestBidId}
                canManage={canManage}
                canRevive={canRevive}
                canPick={showCTA && c.state === 'answered'}
                fxRates={fxRates}
                onAddBid={() => onAddBid(c)}
                onEditBid={(quote) => onEditBid(c, quote)}
                onDeleteBid={(quote) => {
                  void onDeleteBid(c, quote);
                }}
                onReject={() => onReject(c)}
                onReset={() => {
                  void onReset(c);
                }}
                onDelete={() => {
                  void onDelete(c);
                }}
                onPickBid={(quote) => pickWinnerMutation.mutate(quote.id)}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
